import type { Station } from '@eon/types';

const IN_USE_STATUSES = new Set([
  'charging',
  'preparing',
  'finishing',
  'suspended_ev',
  'suspended_evse',
  'reserved',
]);

export function parseCoordinate(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Returns coordinates when the station has a usable map location. */
export function stationCoordinates(station: Station): { lat: number; lng: number } | null {
  const lat = parseCoordinate(station.latitude);
  const lng = parseCoordinate(station.longitude);
  if (lat == null || lng == null) return null;
  if (lat === 0 && lng === 0) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

export interface StationDeviceSummary {
  total: number;
  available: number;
  inUse: number;
  offline: number;
}

export function summarizeStationDevices(station: Station): StationDeviceSummary {
  const devices = station.devices ?? [];
  let available = 0;
  let inUse = 0;
  let offline = 0;

  for (const device of devices) {
    const status = (device.status ?? 'unknown').toLowerCase();
    if (status === 'available') available += 1;
    else if (IN_USE_STATUSES.has(status)) inUse += 1;
    else offline += 1;
  }

  const total = devices.length > 0 ? devices.length : station.deviceCount;
  return { total, available, inUse, offline };
}

/** Green when at least one device is available; red otherwise. */
export function stationMapVariant(station: Station): 'available' | 'unavailable' {
  if (!station.active) return 'unavailable';
  const { available, total } = summarizeStationDevices(station);
  if (total === 0) return 'unavailable';
  return available > 0 ? 'available' : 'unavailable';
}
